/**
 * Revival Curves — Wishlist
 * localStorage-based. Works for all visitors, no login required.
 * Stores product IDs as a JSON array in localStorage key 'rc_wishlist'.
 */

const RCWishlist = (() => {
  const STORAGE_KEY = 'rc_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isWishlisted(productId) {
    return getWishlist().includes(String(productId));
  }

  function toggle(productId) {
    const id = String(productId);
    let list = getWishlist();
    if (list.includes(id)) {
      list = list.filter(i => i !== id);
    } else {
      list.push(id);
    }
    saveWishlist(list);
    return list.includes(id); // returns new state
  }

  function remove(productId) {
    const id = String(productId);
    saveWishlist(getWishlist().filter(i => i !== id));
  }

  function count() {
    return getWishlist().length;
  }

  return { getWishlist, isWishlisted, toggle, remove, count };
})();


/**
 * Wishlist Button — product page heart button
 * Expects: <button class="rc-wishlist-btn" data-product-id="...">
 */
function initWishlistButton() {
  const btn = document.querySelector('.rc-wishlist-btn');
  if (!btn) return;

  const productId = btn.dataset.productId;
  if (!productId) return;

  // Set initial state
  updateButtonState(btn, RCWishlist.isWishlisted(productId));

  btn.addEventListener('click', () => {
    const nowWishlisted = RCWishlist.toggle(productId);
    updateButtonState(btn, nowWishlisted);

    // Animate
    btn.classList.add('rc-wishlist-btn--pulse');
    setTimeout(() => btn.classList.remove('rc-wishlist-btn--pulse'), 400);

    // Update any nav/header count badges
    updateWishlistCountBadges();
  });
}

function updateButtonState(btn, isActive) {
  btn.classList.toggle('rc-wishlist-btn--active', isActive);
  btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
  btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  const label = btn.querySelector('.rc-wishlist-btn__label');
  if (label) {
    label.textContent = isActive ? 'Wishlisted' : 'Add to Wishlist';
  }
}

function updateWishlistCountBadges() {
  const count = RCWishlist.count();
  document.querySelectorAll('.rc-wishlist-count').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
    // Force dimensions inline to prevent any theme override making it oval
    if (count > 0) {
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.minWidth = '0';
      el.style.maxWidth = '20px';
    }
  });
}


/**
 * Wishlist Page ─ renders saved products at /pages/wishlist
 * The page template sets window.RC_WISHLIST_PAGE = true
 */
async function initWishlistPage() {
  if (!window.RC_WISHLIST_PAGE) return;

  const container = document.getElementById('rc-wishlist-items');
  const emptyState = document.getElementById('rc-wishlist-empty');
  if (!container) return;

  const ids = RCWishlist.getWishlist();

  if (ids.length === 0) {
    container.style.display = 'none';
    if (emptyState) emptyState.style.display = 'block';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';
  container.innerHTML = '<p class="rc-wishlist-loading">Loading your wishlist…</p>';

  try {
    const products = await fetchProductsByIds(ids);

    if (products.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    renderWishlistItems(container, products);
  } catch (err) {
    container.innerHTML = '<p>Something went wrong loading your wishlist. Please try again.</p>';
    console.error('Wishlist load error:', err);
  }
}

/**
 * Fetch product data for an array of product IDs using Shopify's AJAX API.
 * We store the handle in localStorage alongside the ID for direct lookup.
 */
async function fetchProductsByIds(ids) {
  const stored = JSON.parse(localStorage.getItem('rc_wishlist_handles') || '{}');
  const products = [];

  await Promise.all(ids.map(async (id) => {
    const handle = stored[id];
    if (handle) {
      try {
        const r = await fetch(`/products/${handle}.js`);
        if (r.ok) {
          const p = await r.json();
          products.push(p);
        }
      } catch {}
    }
  }));

  return products;
}

function renderWishlistItems(container, products) {
  container.innerHTML = '';
  const grid = document.createElement('ul');
  grid.className = 'rc-wishlist-grid';

  products.forEach(product => {
    const img = product.featured_image || (product.images && product.images[0]) || '';
    const price = formatMoney(product.price);
    const comparePrice = product.compare_at_price && product.compare_at_price > product.price
      ? formatMoney(product.compare_at_price) : null;
    const available = product.available;
    const url = `/products/${product.handle}`;

    const variantId = product.variants && product.variants[0] ? product.variants[0].id : '';

    const li = document.createElement('li');
    li.className = 'rc-wishlist-item';
    li.innerHTML = `
      <a href="${url}" class="rc-wishlist-item__link">
        <div class="rc-wishlist-item__img-wrap">
          ${img ? `<img src="${img}" alt="${escapeHtml(product.title)}" loading="lazy" width="300" height="400">` : '<div class="rc-wishlist-item__no-img"></div>'}
          ${!available ? '<span class="rc-wishlist-item__sold-out">Sold Out</span>' : ''}
        </div>
        <div class="rc-wishlist-item__info">
          <p class="rc-wishlist-item__title">${escapeHtml(product.title)}</p>
          <p class="rc-wishlist-item__price">
            ${comparePrice ? `<s class="rc-wishlist-item__compare-price">${comparePrice}</s> ` : ''}
            <span>${price}</span>
          </p>
        </div>
      </a>
      <button class="rc-wishlist-item__remove" data-product-id="${product.id}" aria-label="Remove from wishlist">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      ${available && variantId ? `<button class="rc-wishlist-item__atc" data-variant-id="${variantId}" aria-label="Add to cart">Add to Cart</button>` : `<button class="rc-wishlist-item__atc" disabled>Sold Out</button>`}
    `;
    grid.appendChild(li);
  });

  container.appendChild(grid);

  // Wire up remove buttons
  // Remove buttons
  container.querySelectorAll('.rc-wishlist-item__remove').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.productId;
      RCWishlist.remove(String(id));
      const handles = JSON.parse(localStorage.getItem('rc_wishlist_handles') || '{}');
      delete handles[id];
      localStorage.setItem('rc_wishlist_handles', JSON.stringify(handles));
      btn.closest('.rc-wishlist-item').remove();
      updateWishlistCountBadges();
      if (container.querySelectorAll('.rc-wishlist-item').length === 0) {
        const emptyState = document.getElementById('rc-wishlist-empty');
        if (emptyState) emptyState.style.display = 'block';
        container.style.display = 'none';
      }
    });
  });

  // Add to Cart buttons
  container.querySelectorAll('.rc-wishlist-item__atc').forEach(btn => {
    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.textContent = 'Adding…';
      btn.disabled = true;
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (r.ok) {
          btn.textContent = 'Added to Cart ✓';
          btn.disabled = true;
          btn.style.background = '#5e1f6e';
          // Update cart count bubble if present
          const cartBubble = document.querySelector('.cart-count-bubble span[aria-hidden]');
          if (cartBubble) {
            const countResp = await fetch('/cart.js');
            const cart = await countResp.json();
            cartBubble.textContent = cart.item_count;
          }
        } else {
          btn.textContent = 'Error — try again';
          btn.disabled = false;
        }
      } catch {
        btn.textContent = 'Error — try again';
        btn.disabled = false;
      }
    });
  });
}

function formatMoney(cents) {
  if (typeof cents !== 'number') return '';
  return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}


/**
 * Store product handle alongside ID so the wishlist page can fetch it
 */
function storeProductHandle(productId, productHandle) {
  if (!productId || !productHandle) return;
  const handles = JSON.parse(localStorage.getItem('rc_wishlist_handles') || '{}');
  handles[String(productId)] = productHandle;
  localStorage.setItem('rc_wishlist_handles', JSON.stringify(handles));
}


/**
 * Init — works whether DOMContentLoaded has fired or not (handles defer loading)
 */
function rcWishlistInit() {
  const btn = document.querySelector('.rc-wishlist-btn');
  if (btn && btn.dataset.productId && btn.dataset.productHandle) {
    storeProductHandle(btn.dataset.productId, btn.dataset.productHandle);
  }
  initWishlistButton();
  updateWishlistCountBadges();
  initWishlistPage();
}

// Safe init: if DOM is ready run now, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rcWishlistInit);
} else {
  rcWishlistInit();
}
